import * as React from "react";
import { Flex, Box } from "grid-styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTwitter,
  faFacebook,
  faLinkedin
} from "@fortawesome/free-brands-svg-icons";
import { transparentize } from "polished";
import { Link } from "react-router-dom";

import styled from "../../styled-components";
import { media } from "../../breakpoints";
import { track } from "../../analytics";

const FooterContainer = Flex.extend`
  background-color: #2d2d2d;
  min-height: 350px;
  font-family: ${props => props.theme.fonts.sans};
  padding: 100px 0 50px 0;

  ${media.TABLET`
    padding: 50px 0;
  `};
`;

const Main = Flex.extend`
  color: ${props => props.theme.colors.white};
  flex-direction: row;

  ${media.TABLET`
    flex-direction: column;

  `};
`;

const WaitlistSignUpFormContainer = Box.extend<{ hideOnMobile?: boolean }>`
  max-width: 470px;
  width: 100%;
  margin-right: 25px;
  padding: 0 0 0 55px;

  ${media.TABLET`
  display: ${(props: { hideOnMobile?: boolean }) =>
    props.hideOnMobile ? "none" : "auto"};
    width: 100%;
    padding: 0 20px;
    max-width: 100%;
    margin-right: 0;
    margin-bottom: 50px;
  `};
`;

const MobileDivider = styled.div<{ hideOnMobile?: boolean }>`
  display: none;

  ${media.TABLET`
    width: 100%;
    height: 1px;
    margin-bottom: 50px;
    background-color: ${transparentize(0.8, "#c9c6c1")};
    display: ${(props: { hideOnMobile?: boolean }) =>
      props.hideOnMobile ? "none" : "auto"};
  `};
`;

const LinksContainer = Flex.extend`
  padding-top: 0px;
  padding: 0 55px 0 0;
  flex-direction: row;
  ${media.TABLET`
    padding-top: 50px;
    width: 100%;
    padding: 0 20px;
    flex-direction: column;
  `};
`;

const LinkSection = Flex.extend`
  flex-direction: column;
  &:nth-child(2) {
    margin: 0 65px;
  }
  ${media.TABLET`
    margin-bottom: 25px;
    &:nth-child(2) {
      margin: 0 0 25px 0;
    }
  `};
`;

const SocialContainer = Flex.extend`
  border-top: 1px solid ${transparentize(0.8, "#c9c6c1")};
  margin-top: 95px;
  padding: 35px 55px 0 55px;
  ${media.TABLET`
    margin-top: 25px;
    padding: 35px 20px 0 20px;
  `};
`;

const FooterLink = styled(Link)`
  color: ${props => props.theme.colors.white};
  font-weight: 300;
  font-size: 14px;
  text-decoration: none;
  line-height: 30px;
`;

const FooterLinkA = styled.a`
  color: ${props => props.theme.colors.white};
  font-weight: 300;
  font-size: 14px;
  text-decoration: none;
  line-height: 30px;
`;

const LinkHeader = styled.p`
  font-weight: 600;
  color: ${props => props.theme.colors.white};
  font-size: 14px;
  line-height: 30px;
  margin: 0;
`;

const CopyRight = Box.extend`
  color: ${props => transparentize(0.5, props.theme.colors.white)};
  font-size: 10px;
  text-transform: uppercase;
  margin-left: auto;
`;

const links = [
  {
    header: "Company",
    sublinks: [
      { name: "Careers", slug: "/careers" },
      { name: "Contact Us", slug: "mailto:hello@saytechnologies.com" }
    ]
  },
  {
    header: "More",
    sublinks: [
      { name: "Terms", slug: "/terms" },
      { name: "Privacy", slug: "/privacy" }
    ]
  }
];

export interface IFooter {
  backgroundColor?: string;
  hideFormOnMobile?: boolean;
}

const Footer: React.SFC<IFooter> = ({ backgroundColor, hideFormOnMobile }) => {
  const handleElementClick = (destination: string) => {
    track(`Footer Click: ${destination}`, { destination });
  };

  return (
    <FooterContainer flexDirection="column" width={1}>
      <Main alignItems="flex-start" justifyContent="space-between" width={1}>
        <WaitlistSignUpFormContainer hideOnMobile={hideFormOnMobile} />

        <MobileDivider hideOnMobile={hideFormOnMobile} />

        <LinksContainer>
          {links.map((link, i) => {
            return (
              <LinkSection key={`Footer-Link-Column-${i}`}>
                <LinkHeader>{link.header}</LinkHeader>
                {link.sublinks.map((sublink, j) => {
                  if (
                    sublink.slug.includes(":") ||
                    sublink.slug.includes("#")
                  ) {
                    return (
                      <FooterLinkA
                        href={`${sublink.slug}`}
                        key={`Link-${j}`}
                        onClick={() => {
                          handleElementClick(sublink.slug);
                        }}
                      >
                        {sublink.name}
                      </FooterLinkA>
                    );
                  } else {
                    return (
                      <FooterLink
                        to={`${sublink.slug}`}
                        key={`Link-${j}`}
                        onClick={() => {
                          handleElementClick(sublink.slug);
                        }}
                      >
                        {sublink.name}
                      </FooterLink>
                    );
                  }
                })}
              </LinkSection>
            );
          })}
        </LinksContainer>
      </Main>

      <SocialContainer
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
        width={1}
      >
        <Box>
          <a
            href="https://www.facebook.com/sayapp/"
            onClick={() => {
              handleElementClick("https://www.facebook.com/sayapp/");
            }}
            target="_blank"
          >
            <FontAwesomeIcon icon={faFacebook} color="#A7A7A8" size="2x" />
          </a>
        </Box>
        <Box mx={32}>
          <a
            href="https://www.linkedin.com/company/asayinc/"
            onClick={() => {
              handleElementClick("https://www.linkedin.com/company/asayinc/");
            }}
            target="_blank"
          >
            <FontAwesomeIcon icon={faLinkedin} color="#A7A7A8" size="2x" />
          </a>
        </Box>
        <Box>
          <a
            href="https://twitter.com/say"
            onClick={() => {
              handleElementClick("https://twitter.com/say");
            }}
            target="_blank"
          >
            <FontAwesomeIcon icon={faTwitter} color="#A7A7A8" size="2x" />
          </a>
        </Box>
        <CopyRight alignSelf="center">
          Say &copy; {new Date().getFullYear()}
        </CopyRight>
      </SocialContainer>
    </FooterContainer>
  );
};

export default Footer;



// WEBPACK FOOTER //
// ./src/components/Footer/Footer.tsx